/**
 * Navigation Hooks - A365 Core
 * 
 * Hooks personalizados para facilitar el uso del servicio de navegación
 * en componentes React.
 * 
 * @author A365 Core Team
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { navigationService } from '../services/navigationService';

/**
 * Hook para observar el estado completo de navegación
 * @returns {Object} Estado actual de navegación
 */
export const useNavigationState = () => {
  const [navigationState, setNavigationState] = useState(
    navigationService.getCurrentState()
  );

  useEffect(() => {
    const subscription = navigationService.subscribe(setNavigationState);
    return () => subscription.unsubscribe();
  }, []);

  return navigationState;
};

/**
 * Hook específico para el botón de retroceso
 * @returns {Object} Estado y funciones del botón de retroceso
 */
export const useBackButton = () => {
  const navigationState = useNavigationState();
  
  const showBackButton = useCallback((action, options = {}) => {
    navigationService.showBackButton(action, options);
  }, []);

  const hideBackButton = useCallback(() => {
    navigationService.hideBackButton();
  }, []);

  const setBackButtonEnabled = useCallback((enabled) => {
    navigationService.setBackButtonEnabled(enabled);
  }, []);

  const executeBackAction = useCallback(() => {
    navigationService.executeBackAction();
  }, []);

  const showBackButtonToPrevious = useCallback((options = {}) => {
    navigationService.showBackButtonToPrevious(options);
  }, []);

  const navigateToPrevious = useCallback(() => {
    navigationService.navigateToPrevious();
  }, []);

  return {
    backButton: navigationState.backButton,
    showBackButton,
    hideBackButton,
    setBackButtonEnabled,
    executeBackAction,
    showBackButtonToPrevious,
    navigateToPrevious
  };
};

/**
 * Hook para control completo del servicio de navegación
 * @returns {Object} Todas las funciones y estado del servicio
 */
export const useNavigation = () => {
  const navigationState = useNavigationState();

  const setBackButton = useCallback((config) => {
    navigationService.setBackButton(config);
  }, []);

  const showBackButton = useCallback((action, options = {}) => {
    navigationService.showBackButton(action, options);
  }, []);

  const hideBackButton = useCallback(() => {
    navigationService.hideBackButton();
  }, []);

  const executeBackAction = useCallback(() => {
    navigationService.executeBackAction();
  }, []);

  const resetNavigation = useCallback(() => {
    navigationService.reset();
  }, []);

  const showBackButtonToPrevious = useCallback((options = {}) => {
    navigationService.showBackButtonToPrevious(options);
  }, []);

  const navigateToPrevious = useCallback(() => {
    navigationService.navigateToPrevious();
  }, []);

  return {
    navigationState,
    setBackButton,
    showBackButton,
    hideBackButton,
    executeBackAction,
    resetNavigation,
    showBackButtonToPrevious,
    navigateToPrevious,
    // Acceso directo al botón de retroceso
    backButton: navigationState.backButton
  };
};
